import { useState, useEffect, useCallback } from "react";
import { PaginatedResponse } from "../types/api";

export type FetchFunction<T> = (
  page: number,
  pageSize: number,
  search: string
) => Promise<PaginatedResponse<T>>;

export interface UseServerSidePaginationOptions {
  initialPageSize?: number;
  debounceMs?: number;
  onError?: (error: any) => void;
}

export interface UseServerSidePaginationReturn<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  isLoading: boolean;
  searchTerm: string;
  handlePageChange: (newPage: number) => void;
  handlePageSizeChange: (newPageSize: number) => void;
  handleSearchChange: (search: string) => void;
  refresh: () => void;
}

export function useServerSidePagination<T>(
  fetchFunction: FetchFunction<T>,
  options: UseServerSidePaginationOptions = {}
): UseServerSidePaginationReturn<T> {
  const {
    initialPageSize = 10,
    debounceMs = 500,
    onError,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const fetchData = useCallback(
    async (pageNum: number, pageSizeNum: number, search: string) => {
      setIsLoading(true);
      try {
        const response = await fetchFunction(pageNum, pageSizeNum, search);
        setData(response.results);
        setTotalCount(response.count);
      } catch (error: any) {
        console.error("Error fetching paginated data:", error);
        if (onError) {
          onError(error);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFunction, onError]
  );

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, debounceMs]);

  // Reset to first page when debounced search term changes
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Fetch data when page, pageSize, or debouncedSearchTerm changes
  useEffect(() => {
    fetchData(page, pageSize, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearchTerm]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when page size changes
  }, []);

  const handleSearchChange = useCallback((search: string) => {
    setSearchTerm(search);
  }, []);

  const refresh = useCallback(() => {
    fetchData(page, pageSize, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, page, pageSize, debouncedSearchTerm]);

  return {
    data,
    page,
    pageSize,
    totalCount,
    isLoading,
    searchTerm,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    refresh,
  };
}

